(function (global) {
  'use strict';

  const DEFAULT_NORMALISER_VERSION = '1.0.0';
  const DEFAULT_BRAND_LIMIT = 100;
  const DEFAULT_PRODUCT_LIMIT = 160;
  const STRUCTURED_ANSWER_KEYS = [
    'currentFoodType',
    'currentFoodBrandSelection',
    'currentFoodProtein',
    'currentFoodDuration',
    'primarySwitchReason',
    'concernTiming',
    'concernTrend'
  ];

  function isObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim() !== '';
  }

  function getCurrentFoodConfig(config) {
    return config && isObject(config.currentFoodIntelligence)
      ? config.currentFoodIntelligence
      : {};
  }

  function getPositiveInteger(value, fallback) {
    return Number.isInteger(value) && value > 0 ? value : fallback;
  }

  function getInputLimits(config) {
    const currentFood = getCurrentFoodConfig(config);
    const limits = isObject(currentFood.inputLimits) ? currentFood.inputLimits : {};

    return {
      brandRawMaxLength: getPositiveInteger(limits.brandRawMaxLength, DEFAULT_BRAND_LIMIT),
      productRawMaxLength: getPositiveInteger(limits.productRawMaxLength, DEFAULT_PRODUCT_LIMIT)
    };
  }

  function stripControlCharacters(value) {
    return value.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
  }

  function truncateCodePoints(value, maximumLength) {
    return Array.from(value).slice(0, maximumLength).join('');
  }

  function sanitiseFreeText(value, maximumLength) {
    if (typeof value !== 'string') {
      return null;
    }

    const normalisedUnicode = typeof value.normalize === 'function' ? value.normalize('NFKC') : value;
    const safeValue = truncateCodePoints(
      stripControlCharacters(normalisedUnicode).trim(),
      getPositiveInteger(maximumLength, DEFAULT_PRODUCT_LIMIT)
    ).trim();

    return safeValue || null;
  }

  function createComparisonKey(value) {
    const safeValue = sanitiseFreeText(value, DEFAULT_PRODUCT_LIMIT);
    if (!safeValue) {
      return '';
    }

    return safeValue
      .toLocaleLowerCase('en-AU')
      .replace(/['\u2019]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function getBrands(config) {
    const currentFood = getCurrentFoodConfig(config);
    return Array.isArray(currentFood.brands) ? currentFood.brands : [];
  }

  function getQuestionOptions(config, answerKey) {
    const questions = config && Array.isArray(config.questions) ? config.questions : [];
    const question = questions.find(function (candidate) {
      return candidate && candidate.id === answerKey;
    });

    return question && Array.isArray(question.options)
      ? question.options.map(function (option) { return option && option.value; })
      : [];
  }

  function findBrandById(config, brandId) {
    return getBrands(config).find(function (brand) {
      return brand && brand.id === brandId;
    }) || null;
  }

  function findBrandByAlias(config, rawBrand) {
    const comparisonKey = createComparisonKey(rawBrand);
    if (!comparisonKey) {
      return null;
    }

    return getBrands(config).find(function (brand) {
      if (!brand || brand.id === 'other' || brand.id === 'unknown') {
        return false;
      }

      const aliases = Array.isArray(brand.aliases) ? brand.aliases : [];
      return aliases.some(function (alias) {
        return createComparisonKey(alias) === comparisonKey;
      });
    }) || null;
  }

  function sanitiseCurrentFoodAnswers(answers, config) {
    const source = isObject(answers) ? answers : {};
    const safeAnswers = {};
    const limits = getInputLimits(config);

    Object.keys(source).forEach(function (key) {
      safeAnswers[key] = source[key];
    });

    STRUCTURED_ANSWER_KEYS.forEach(function (answerKey) {
      if (!Object.prototype.hasOwnProperty.call(safeAnswers, answerKey)) {
        return;
      }

      const allowedValues = getQuestionOptions(config, answerKey);
      if (typeof safeAnswers[answerKey] !== 'string' || !allowedValues.includes(safeAnswers[answerKey])) {
        delete safeAnswers[answerKey];
      }
    });

    if (Object.prototype.hasOwnProperty.call(safeAnswers, 'currentFoodBrandRaw')) {
      const brandRaw = sanitiseFreeText(safeAnswers.currentFoodBrandRaw, limits.brandRawMaxLength);
      if (brandRaw) {
        safeAnswers.currentFoodBrandRaw = brandRaw;
      } else {
        delete safeAnswers.currentFoodBrandRaw;
      }
    }

    if (Object.prototype.hasOwnProperty.call(safeAnswers, 'currentFoodProductRaw')) {
      const productRaw = sanitiseFreeText(safeAnswers.currentFoodProductRaw, limits.productRawMaxLength);
      if (productRaw) {
        safeAnswers.currentFoodProductRaw = productRaw;
      } else {
        delete safeAnswers.currentFoodProductRaw;
      }
    }

    return safeAnswers;
  }

  function normaliseBrand(answers, config) {
    const selection = answers.currentFoodBrandSelection;
    const rawBrand = selection === 'other' || !selection
      ? answers.currentFoodBrandRaw || null
      : null;
    const selectedBrand = findBrandById(config, selection);
    const aliasBrand = rawBrand ? findBrandByAlias(config, rawBrand) : null;

    if (selectedBrand && selection !== 'other' && selection !== 'unknown') {
      return {
        rawBrand: null,
        normalisedBrandId: selection,
        brandMatchStatus: 'selected_known_brand'
      };
    }

    if (selection === 'unknown') {
      return {
        rawBrand: null,
        normalisedBrandId: 'unknown',
        brandMatchStatus: 'unknown'
      };
    }

    if (aliasBrand) {
      return {
        rawBrand: rawBrand,
        normalisedBrandId: aliasBrand.id,
        brandMatchStatus: 'exact_alias'
      };
    }

    if (rawBrand) {
      return {
        rawBrand: rawBrand,
        normalisedBrandId: 'other',
        brandMatchStatus: 'other_raw'
      };
    }

    return {
      rawBrand: null,
      normalisedBrandId: selection === 'other' ? 'other' : null,
      brandMatchStatus: 'not_supplied'
    };
  }

  function normaliseProduct(answers, normalisedBrandId) {
    const rawProduct = answers.currentFoodProductRaw || null;
    const productKey = createComparisonKey(rawProduct);

    if (productKey === 'unknown' || productKey === 'not sure' || productKey === 'im not sure') {
      return {
        rawProduct: rawProduct,
        normalisedProductId: null,
        productMatchStatus: 'unknown'
      };
    }

    if (rawProduct) {
      return {
        rawProduct: rawProduct,
        normalisedProductId: null,
        productMatchStatus: 'raw_unmatched'
      };
    }

    if (normalisedBrandId && normalisedBrandId !== 'unknown' && normalisedBrandId !== 'other') {
      return {
        rawProduct: null,
        normalisedProductId: null,
        productMatchStatus: 'known_brand_unknown_product'
      };
    }

    return {
      rawProduct: null,
      normalisedProductId: null,
      productMatchStatus: normalisedBrandId === 'unknown' ? 'unknown' : 'not_supplied'
    };
  }

  function normaliseCurrentFood(answers, config) {
    const safeAnswers = sanitiseCurrentFoodAnswers(answers, config);
    const currentFood = getCurrentFoodConfig(config);
    const brand = normaliseBrand(safeAnswers, config);
    const product = normaliseProduct(safeAnswers, brand.normalisedBrandId);

    return {
      rawBrand: brand.rawBrand,
      rawProduct: product.rawProduct,
      normalisedBrandId: brand.normalisedBrandId,
      normalisedProductId: product.normalisedProductId,
      brandMatchStatus: brand.brandMatchStatus,
      productMatchStatus: product.productMatchStatus,
      foodTypeId: safeAnswers.currentFoodType || null,
      proteinId: safeAnswers.currentFoodProtein || null,
      durationId: safeAnswers.currentFoodDuration || null,
      switchReasonIds: safeAnswers.primarySwitchReason ? [safeAnswers.primarySwitchReason] : [],
      concernTimingId: safeAnswers.concernTiming || null,
      concernTrendId: safeAnswers.concernTrend || null,
      normaliserVersion: isNonEmptyString(currentFood.normaliserVersion)
        ? currentFood.normaliserVersion
        : DEFAULT_NORMALISER_VERSION
    };
  }

  function validateCurrentFoodConfig(config) {
    const errors = [];
    const currentFood = getCurrentFoodConfig(config);
    const brands = getBrands(config);
    const brandIds = new Set();
    const aliasKeys = new Set();
    const limits = getInputLimits(config);

    if (!isNonEmptyString(currentFood.schemaVersion)) {
      errors.push('currentFoodIntelligence.schemaVersion is required.');
    }
    if (!isNonEmptyString(currentFood.normaliserVersion)) {
      errors.push('currentFoodIntelligence.normaliserVersion is required.');
    }
    if (!brands.length) {
      errors.push('currentFoodIntelligence.brands must not be empty.');
    }
    if (!Number.isInteger(limits.brandRawMaxLength) || !Number.isInteger(limits.productRawMaxLength)) {
      errors.push('currentFoodIntelligence input limits must be positive integers.');
    }

    brands.forEach(function (brand, brandIndex) {
      const path = 'currentFoodIntelligence.brands[' + brandIndex + ']';
      if (!isObject(brand) || !isNonEmptyString(brand.id) || !isNonEmptyString(brand.labelKey)) {
        errors.push(path + ' must include stable id and labelKey values.');
        return;
      }
      if (brandIds.has(brand.id)) {
        errors.push('currentFoodIntelligence.brands must not contain duplicate ids.');
      }
      brandIds.add(brand.id);

      if (!Array.isArray(brand.aliases)) {
        errors.push(path + '.aliases must be an array.');
        return;
      }

      brand.aliases.forEach(function (alias) {
        const aliasKey = createComparisonKey(alias);
        if (!aliasKey) {
          errors.push(path + '.aliases must contain non-empty strings.');
        } else if (aliasKeys.has(aliasKey)) {
          errors.push('currentFoodIntelligence.brands aliases must be unique after deterministic normalisation.');
        } else {
          aliasKeys.add(aliasKey);
        }
      });
    });

    ['other', 'unknown'].forEach(function (requiredId) {
      if (!brandIds.has(requiredId)) {
        errors.push('currentFoodIntelligence.brands must include ' + requiredId + '.');
      }
    });

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  const api = {
    sanitiseFreeText: sanitiseFreeText,
    sanitiseCurrentFoodAnswers: sanitiseCurrentFoodAnswers,
    normaliseCurrentFood: normaliseCurrentFood,
    validateCurrentFoodConfig: validateCurrentFoodConfig
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.CurrentFoodNormalizer = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
